import { useState, useRef, useEffect, memo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { meetingRoomsApi, MeetingRoomEvent, MeetingRoomSchedule } from "@/lib/api/client";
import { Button, ActionIcon, Text, Title, Group, Box, Flex, Stack } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ChevronLeft, ChevronRight, Camera, Tv, Volume2, Users, X, Maximize2 } from "lucide-react";
import { MeetingNav } from "@/components/meeting-nav";
import { useAuthStore } from "@/lib/auth";
import { toast } from "sonner";
import {
  RoomDaySchedule,
  BookingDialog,
  RoomPriceBadge,
  hasPriceInfo,
  priceBadgeText,
  type ScheduleEvent,
  type ScheduleFreeSlot,
} from "@nodus/booking";
import { useApprovalSettings } from "@/hooks/use-booking-approvals";
import { approvalNoticeFor, bookingResultMessage } from "@/lib/booking-approvals";

// Timeline config
const SLOT_MINUTES = 15;
const DAY_START_HOUR = 0; // 00:00
const DAY_END_HOUR = 24; // 24:00
const TOTAL_SLOTS = ((DAY_END_HOUR - DAY_START_HOUR) * 60) / SLOT_MINUTES;
const SLOT_WIDTH_PX = 32;
const ROW_LABEL_WIDTH = 160;

function toISODate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number) {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

function slotIndex(isoDatetime: string, date: Date): number {
  const dt = new Date(isoDatetime);
  const dayStart = new Date(date);
  dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
  return Math.max(0, (dt.getTime() - dayStart.getTime()) / (SLOT_MINUTES * 60 * 1000));
}

function eventSlots(event: MeetingRoomEvent, date: Date): { start: number; end: number } {
  const s = slotIndex(event.start, date);
  const e = slotIndex(event.end, date);
  return {
    start: Math.max(0, Math.min(s, TOTAL_SLOTS)),
    end: Math.max(0, Math.min(e, TOTAL_SLOTS)),
  };
}


interface Selection {
  roomId: number;
  startSlot: number;
  endSlot: number;
}

interface TimelineDialog {
  type: "book" | "event";
  x: number;
  y: number;
  // book mode
  roomId: number;
  roomName: string;
  start: string;
  end: string;
  // event mode
  event?: MeetingRoomEvent;
}

function formatHour(slotIdx: number) {
  const totalMinutes = DAY_START_HOUR * 60 + slotIdx * SLOT_MINUTES;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function slotToIso(date: Date, slot: number): string {
  const d = new Date(date);
  const totalMinutes = DAY_START_HOUR * 60 + slot * SLOT_MINUTES;
  d.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
  return d.toISOString();
}

const RoomRow = memo(function RoomRow({
  room,
  date,
  selection,
  pastSlotThreshold,
  onSlotMouseDown,
  onSlotMouseEnter,
  onEventClick,
  onRoomClick,
}: {
  room: MeetingRoomSchedule;
  date: Date;
  selection: Selection | null;
  pastSlotThreshold: number;
  onSlotMouseDown: (roomId: number, slot: number) => void;
  onSlotMouseEnter: (slot: number) => void;
  onEventClick: (event: MeetingRoomEvent, x: number, y: number) => void;
  onRoomClick?: (roomId: number) => void;
}) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const bookedSlots = new Set<number>();
  for (const event of room.events) {
    const { start, end } = eventSlots(event, date);
    for (let i = Math.floor(start); i < Math.ceil(end); i++) {
      bookedSlots.add(i);
    }
  }

  const isSelecting = selection?.roomId === room.id;

  const amenities = [
    room.seats != null && <span key="seats" style={{ display: "flex", alignItems: "center", gap: 2 }}><Users size={12} />{room.seats}</span>,
    room.has_camera && <Camera key="cam" size={12} />,
    room.has_tv && <Tv key="tv" size={12} />,
    room.has_speaker && <Volume2 key="spk" size={12} />,
  ].filter(Boolean);

  return (
    <div style={{ display: "flex", height: 40 }}>
      {/* Room label -- entire cell clickable */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexShrink: 0,
          paddingRight: 8,
          borderRight: "1px solid var(--mantine-color-default-border)",
          overflow: "hidden",
          background: "var(--mantine-color-body)",
          zIndex: 10,
          cursor: "pointer",
          width: ROW_LABEL_WIDTH,
          position: "sticky",
          left: 0,
          transition: "background 150ms",
        }}
        onClick={() => onRoomClick?.(room.id)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--mantine-color-default-hover)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--mantine-color-body)")}
      >
        {amenities.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, flexShrink: 0, color: "var(--mantine-color-dimmed)", fontSize: 10 }}>
            {amenities.slice(0, 4)}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <Text fz="xs" fw={500} truncate="end" style={{ display: "block" }}>{room.description || room.name}</Text>
          {/* A priced room's price takes the second line (the row is 40px high): members see it before
              they drag out a slot. */}
          {hasPriceInfo(room.pricing) ? (
            <Text fz="xs" fw={600} c={room.pricing!.paid ? "orange" : "teal"} truncate="end" style={{ display: "block" }} title={room.pricing!.label}>
              {priceBadgeText(room.pricing)}
            </Text>
          ) : room.description && (
            <Text fz="xs" c="dimmed" truncate="end" style={{ display: "block" }}>{room.name}</Text>
          )}
        </div>
      </div>

      {/* Slots + event overlay */}
      <div style={{ position: "relative", width: TOTAL_SLOTS * SLOT_WIDTH_PX, height: 40 }}>
        {/* Background grid — single canvas div with event delegation */}
        <div
          style={{
            position: "absolute", inset: 0,
            backgroundSize: `${SLOT_WIDTH_PX}px 40px`,
            backgroundImage: "linear-gradient(to right, rgba(128,128,128,0.15) 1px, transparent 1px)",
            borderBottom: "1px solid rgba(128,128,128,0.15)",
          }}
          onMouseDown={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const slot = Math.floor((e.clientX - rect.left) / SLOT_WIDTH_PX);
            if (slot >= 0 && slot < TOTAL_SLOTS && !bookedSlots.has(slot) && slot >= pastSlotThreshold && room.is_bookable) {
              e.preventDefault();
              onSlotMouseDown(room.id, slot);
            }
          }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const slot = Math.floor((e.clientX - rect.left) / SLOT_WIDTH_PX);
            if (slot >= 0 && slot < TOTAL_SLOTS && !bookedSlots.has(slot)) {
              onSlotMouseEnter(slot);
            }
          }}
        >
          {/* Past overlay */}
          {pastSlotThreshold > 0 && (
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: pastSlotThreshold * SLOT_WIDTH_PX, background: "rgba(128,128,128,0.08)", pointerEvents: "none" }} />
          )}
          {/* Unconnected overlay */}
          {!room.is_bookable && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(128,128,128,0.04)", pointerEvents: "none" }} />
          )}
          {/* Free slot tint (only future, bookable) */}
          {room.is_bookable && (() => {
            // Find contiguous free ranges after pastSlotThreshold
            const freeRanges: { start: number; end: number }[] = [];
            let rangeStart = -1;
            for (let i = pastSlotThreshold; i < TOTAL_SLOTS; i++) {
              if (!bookedSlots.has(i)) {
                if (rangeStart < 0) rangeStart = i;
              } else {
                if (rangeStart >= 0) { freeRanges.push({ start: rangeStart, end: i }); rangeStart = -1; }
              }
            }
            if (rangeStart >= 0) freeRanges.push({ start: rangeStart, end: TOTAL_SLOTS });
            return freeRanges.map((r) => (
              <div key={r.start} style={{ position: "absolute", top: 0, bottom: 0, left: r.start * SLOT_WIDTH_PX, width: (r.end - r.start) * SLOT_WIDTH_PX, background: "rgba(34,197,94,0.12)", pointerEvents: "none" }} />
            ));
          })()}
          {/* Selection highlight */}
          {isSelecting && (() => {
            const s = Math.min(selection!.startSlot, selection!.endSlot);
            const e = Math.max(selection!.startSlot, selection!.endSlot) + 1;
            return <div style={{ position: "absolute", top: 0, bottom: 0, left: s * SLOT_WIDTH_PX, width: (e - s) * SLOT_WIDTH_PX, background: "rgba(59,130,246,0.3)", pointerEvents: "none" }} />;
          })()}
        </div>

        {/* Event blocks -- full span, no internal borders */}
        {room.events.map((event) => {
          const { start, end } = eventSlots(event, date);
          if (end <= start) return null;
          const isUnbookable = event.id < 0;
          const isHovered = hoveredId === event.id;
          const widthPx = (end - start) * SLOT_WIDTH_PX - 1;
          const mine = event.is_my_booking;

          let bg: string;
          if (isUnbookable) {
            bg = "repeating-linear-gradient(135deg,var(--mantine-color-default-hover),var(--mantine-color-default-hover) 4px,rgba(128,128,128,0.15) 4px,rgba(128,128,128,0.15) 6px)";
          } else if (mine) {
            bg = isHovered ? "var(--mantine-color-blue-7)" : "var(--mantine-color-blue-5)";
          } else {
            bg = isHovered ? "var(--mantine-color-red-7)" : "var(--mantine-color-red-5)";
          }

          return (
            <div
              key={event.id}
              style={{
                position: "absolute",
                top: 2,
                bottom: 2,
                borderRadius: 2,
                transition: "background 150ms",
                overflow: "hidden",
                left: start * SLOT_WIDTH_PX,
                width: widthPx,
                background: bg,
                opacity: isUnbookable ? 0.7 : 1,
                cursor: isUnbookable ? undefined : "pointer",
              }}
              onMouseEnter={() => !isUnbookable && setHoveredId(event.id)}
              onMouseLeave={() => !isUnbookable && setHoveredId(null)}
              onClick={(e) => !isUnbookable && onEventClick(event, e.clientX, e.clientY)}
            >
              {widthPx >= 60 && (
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  padding: "0 6px",
                  fontSize: 12,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  userSelect: "none",
                  color: isUnbookable ? "var(--mantine-color-dimmed)" : "white",
                }}>
                  {event.title}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default function MeetingRoomsTimelinePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(() => {
    const d = searchParams.get("date");
    return d ? new Date(d + "T00:00:00") : new Date();
  });
  const [selection, setSelection] = useState<Selection | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dialog, setDialog] = useState<TimelineDialog | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(() => {
    const r = searchParams.get("room");
    return r ? Number(r) : null;
  });
  const setSelectedRoomAndUrl = (id: number | null) => {
    setSelectedRoomId(id);
    const params = new URLSearchParams(window.location.search);
    if (id) {
      params.set("room", String(id));
    } else {
      params.delete("room");
    }
    window.history.replaceState({}, "", `?${params}`);
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const { user } = useAuthStore();
  const { data: approvalSettings } = useApprovalSettings();

  const cancelMutation = useMutation({
    mutationFn: (eventId: number) => meetingRoomsApi.cancelBooking(eventId),
    onSuccess: () => {
      setDialog(null);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["meeting-rooms-schedule"] });
        queryClient.invalidateQueries({ queryKey: ["meeting-schedule"] });
        queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      }, 1000);
    },
  });

  const bookMutation = useMutation({
    mutationFn: ({ roomId, start, end, title, description }: { roomId: number; start: string; end: string; title: string; description: string }) => {
      const bookedBy = user ? `Booked by: ${user.email}${user.tenant_name ? ` (${user.tenant_name})` : ""}` : "";
      return meetingRoomsApi.bookRoom(roomId, {
        title,
        start,
        end,
        description: [description, bookedBy].filter(Boolean).join("\n\n"),
      });
    },
    onSuccess: (res) => {
      toast.success(bookingResultMessage(res.data.approval, res.data.start, res.data.site_time_zone));
      setDialog(null);
      setSelection(null);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["meeting-rooms-schedule"] });
        queryClient.invalidateQueries({ queryKey: ["meeting-schedule"] });
        queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      }, 1000);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || "Failed to book room";
      toast.error(msg);
    },
  });

  const dateStr = toISODate(date);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["meeting-rooms-schedule", dateStr],
    queryFn: () => meetingRoomsApi.getSchedule(dateStr),
    staleTime: 0,
    refetchInterval: 60_000,
  });

  const rooms = data?.data ?? [];

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleSlotMouseDown = useCallback((roomId: number, slot: number) => {
    setSelection({ roomId, startSlot: slot, endSlot: slot });
    setIsDragging(true);
    setDialog(null);
  }, []);

  const handleSlotMouseEnter = useCallback((slot: number) => {
    setIsDragging((dragging) => {
      if (dragging) {
        setSelection((prev) => prev ? { ...prev, endSlot: slot } : prev);
      }
      return dragging;
    });
  }, []);

  function handleMouseUp(e?: React.MouseEvent<HTMLDivElement>) {
    if (isDragging && selection) {
      const s = Math.min(selection.startSlot, selection.endSlot);
      const eSlot = Math.max(selection.startSlot, selection.endSlot) + 1;
      const room = rooms.find((r) => r.id === selection.roomId);
      if (room && room.is_bookable) {
        const isPast = s < pastSlotThreshold;
        let hasConflict = isPast;
        if (!hasConflict) {
          const booked = new Set<number>();
          for (const ev of room.events) {
            const { start, end } = eventSlots(ev, date);
            for (let i = Math.floor(start); i < Math.ceil(end); i++) booked.add(i);
          }
          for (let i = s; i < eSlot; i++) {
            if (booked.has(i)) { hasConflict = true; break; }
          }
        }
        if (!hasConflict) {
          const startIso = slotToIso(date, s);
          const endIso = slotToIso(date, eSlot);
          const mx = e?.clientX ?? 400;
          const my = e?.clientY ?? 300;
          setDialog({ type: "book", x: mx, y: my, start: startIso, end: endIso, roomId: room.id, roomName: room.description || room.name });
        }
      }
    }
    setIsDragging(false);
  }

  const pastSlotThreshold = (() => {
    const now = new Date();
    const todayStr = toISODate(now);
    if (toISODate(date) > todayStr) return 0;
    if (toISODate(date) < todayStr) return TOTAL_SLOTS;
    const minutesSinceDayStart = (now.getHours() - DAY_START_HOUR) * 60 + now.getMinutes();
    return Math.max(0, Math.ceil(minutesSinceDayStart / SLOT_MINUTES));
  })();

  const isToday = toISODate(date) === toISODate(today);
  const nowSlot = (now.getHours() - DAY_START_HOUR) * (60 / SLOT_MINUTES) + now.getMinutes() / SLOT_MINUTES;
  const showNowIndicator = isToday && nowSlot >= 0 && nowSlot <= TOTAL_SLOTS;

  useEffect(() => {
    if (!showNowIndicator || !containerRef.current || rooms.length === 0) return;
    const container = containerRef.current;
    const nowPx = ROW_LABEL_WIDTH + nowSlot * SLOT_WIDTH_PX;
    const target = nowPx - container.clientWidth / 2;
    container.scrollLeft = Math.max(0, target);
  }, [showNowIndicator, rooms.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const hourLabels: { slot: number; label: string }[] = [];
  for (let i = 0; i <= TOTAL_SLOTS; i += 4) {
    hourLabels.push({ slot: i, label: formatHour(i) });
  }

  return (
    <Flex h="100%" onMouseUp={handleMouseUp}>
      {/* Main timeline area */}
      <Box p="md" style={{ minWidth: 0, flex: selectedRoomId ? 1 : undefined, width: selectedRoomId ? undefined : "100%" }}>
      <Title order={2} mb="md">Meetings</Title>
      <MeetingNav />
      <Stack gap="md">
      {/* Date nav */}
      <Group gap="xs" wrap="wrap">
        <Group gap={4}>
          <Button variant="subtle" size="compact-sm" onClick={() => setDate(addDays(date, -1))}>
            <ChevronLeft size={16} />
          </Button>
          <Box pos="relative" style={{ cursor: "pointer" }} onClick={() => dateInputRef.current?.showPicker()}>
            <Text fw={600} fz="sm" ta="center" miw={110} td="underline" c="blue">
              {date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
              {toISODate(date) === toISODate(today) && (
                <Text fz="xs" c="dimmed" component="span" ml={4}>(Today)</Text>
              )}
            </Text>
            <input
              ref={dateInputRef}
              type="date"
              value={toISODate(date)}
              onChange={(e) => { if (e.target.value) setDate(new Date(e.target.value + "T00:00:00")); }}
              style={{ position: "absolute", opacity: 0, width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}
            />
          </Box>
          <Button variant="subtle" size="compact-sm" onClick={() => setDate(addDays(date, 1))}>
            <ChevronRight size={16} />
          </Button>
        </Group>
      </Group>

      {isLoading && <Text c="dimmed" fz="sm">Loading...</Text>}
      {isError && <Text fz="sm" c="red">Failed to load schedule.</Text>}

      {!isLoading && !isError && rooms.length === 0 && (
        <Text c="dimmed" fz="sm">No meeting rooms found.</Text>
      )}

      {/* Timeline grid */}
      {rooms.length > 0 && (
        <>
        <Box
          style={{ overflowX: "auto", maxWidth: "100%" }}
          ref={containerRef}
        >
          <div style={{ minWidth: ROW_LABEL_WIDTH + TOTAL_SLOTS * SLOT_WIDTH_PX, position: "relative" }}>
            {/* Hour header */}
            <div style={{ display: "flex", borderBottom: "1px solid var(--mantine-color-default-border)", marginBottom: 4, height: 24 }}>
              <div style={{ width: ROW_LABEL_WIDTH, position: "sticky", left: 0, flexShrink: 0, background: "var(--mantine-color-body)", zIndex: 10 }} />
              <div style={{ position: "relative", flex: 1 }}>
                {hourLabels.map(({ slot, label }, i) => {
                  const isFirst = i === 0;
                  const isLast = i === hourLabels.length - 1;
                  return (
                    <Text
                      key={slot}
                      fz="xs"
                      c="dimmed"
                      style={{
                        position: "absolute",
                        left: slot * SLOT_WIDTH_PX,
                        transform: isFirst ? "none" : isLast ? "translateX(-100%)" : "translateX(-50%)",
                      }}
                    >
                      {label}
                    </Text>
                  );
                })}
              </div>
            </div>

            {/* Room rows grouped by address -> floor */}
            {(() => {
              const byAddress = new Map<string, Map<string, MeetingRoomSchedule[]>>();
              for (const room of rooms) {
                const addr = room.address_name;
                const floor = room.floor_name;
                if (!byAddress.has(addr)) byAddress.set(addr, new Map());
                const byFloor = byAddress.get(addr)!;
                if (!byFloor.has(floor)) byFloor.set(floor, []);
                byFloor.get(floor)!.push(room);
              }
              const multiAddress = byAddress.size > 1;
              return Array.from(byAddress.entries()).map(([addr, floors]) => (
                <div key={addr} style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
                  {multiAddress && (
                    <div style={{ background: "var(--mantine-color-default-hover)", borderBottom: "1px solid var(--mantine-color-default-border)" }}>
                      <Text fz="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em", padding: "4px 8px", position: "sticky", left: 0, width: "fit-content" }}>
                        {addr}
                      </Text>
                    </div>
                  )}
                  {Array.from(floors.entries()).map(([floor, floorRooms]) => (
                    <div key={floor}>
                      <div style={{ background: "rgba(var(--mantine-color-default-hover), 0.2)", borderBottom: "1px solid var(--mantine-color-default-border)" }}>
                        <Text fz="xs" fw={500} c="dimmed" style={{ padding: "2px 8px", position: "sticky", left: 0, width: "fit-content" }}>
                          {floor}
                        </Text>
                      </div>
                      {floorRooms.map((room) => (
                        <div key={room.id} style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
                          <RoomRow
                            room={room}
                            date={date}
                            selection={selection}
                            pastSlotThreshold={pastSlotThreshold}
                            onSlotMouseDown={handleSlotMouseDown}
                            onSlotMouseEnter={handleSlotMouseEnter}
                            onEventClick={(event, x, y) => setDialog({ type: "event", x, y, event, roomId: room.id, roomName: room.description || room.name, start: event.start, end: event.end })}
                            onRoomClick={(id) => isMobile ? navigate(`/meeting-rooms/${id}`) : setSelectedRoomAndUrl(id)}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ));
            })()}

            {/* Current-time indicator */}
            {showNowIndicator && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  zIndex: 10,
                  left: ROW_LABEL_WIDTH + nowSlot * SLOT_WIDTH_PX,
                  width: 2,
                }}
              >
                <div style={{ position: "absolute", inset: 0, background: "var(--mantine-color-red-5)", opacity: 0.8 }} />
                <div style={{ position: "absolute", top: -4, left: -4, width: 12, height: 12, borderRadius: "50%", background: "var(--mantine-color-red-5)" }} />
              </div>
            )}

          </div>
        </Box>

        {/* Legend */}
        <Group gap="md" px="xs" py="xs" style={{ borderTop: "1px solid var(--mantine-color-default-border)", background: "var(--mantine-color-body)" }}>
          <Group gap={4}>
            <Box style={{ display: "inline-block", width: 12, height: 12, borderRadius: 4, background: "var(--mantine-color-green-1)", border: "1px solid var(--mantine-color-default-border)" }} />
            <Text fz="xs" c="dimmed">Free</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ display: "inline-block", width: 12, height: 12, borderRadius: 4, background: "var(--mantine-color-blue-5)", border: "1px solid var(--mantine-color-default-border)" }} />
            <Text fz="xs" c="dimmed">My booking</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ display: "inline-block", width: 12, height: 12, borderRadius: 4, background: "var(--mantine-color-red-5)", border: "1px solid var(--mantine-color-default-border)" }} />
            <Text fz="xs" c="dimmed">Booked</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ display: "inline-block", width: 12, height: 12, borderRadius: 4, background: "var(--mantine-color-blue-3)", border: "1px solid var(--mantine-color-default-border)" }} />
            <Text fz="xs" c="dimmed">Selected</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ display: "inline-block", width: 12, height: 12, borderRadius: 4, background: "var(--mantine-color-default-hover)", border: "1px solid var(--mantine-color-default-border)" }} />
            <Text fz="xs" c="dimmed">Past</Text>
          </Group>
          <Text fz="xs" c="dimmed" ml="auto">Drag to select, release to book</Text>
        </Group>
        </>
      )}

      {/* BookingDialog */}
      {dialog && dialog.type === "book" && (
        <BookingDialog
          x={dialog.x}
          y={dialog.y}
          onDismiss={() => { setDialog(null); setSelection(null); }}
          bookingSlot={{ start: dialog.start, end: dialog.end }}
          onBook={(start, end, title, description) => {
            bookMutation.mutate({ roomId: dialog.roomId, start, end, title, description });
          }}
          bookPending={bookMutation.isPending}
          pricing={rooms.find((r) => r.id === dialog.roomId)?.pricing}
          bookingNotice={approvalNoticeFor(approvalSettings, rooms.find((r) => r.id === dialog.roomId)?.pricing)}
        />
      )}
      {dialog && dialog.type === "event" && (
        <BookingDialog
          x={dialog.x}
          y={dialog.y}
          onDismiss={() => setDialog(null)}
          event={{
            id: dialog.event!.id,
            title: dialog.event!.title,
            start: dialog.event!.start,
            end: dialog.event!.end,
            organizerName: dialog.event!.organizer_tenant_name ?? undefined,
            description: dialog.event!.description,
            isMyBooking: dialog.event!.is_my_booking,
          }}
          onCancel={(id) => cancelMutation.mutate(id)}
          cancelPending={cancelMutation.isPending}
          pricing={rooms.find((r) => r.id === dialog.roomId)?.pricing}
        />
      )}
      </Stack>
      </Box>

      {/* Room detail sidebar */}
      {selectedRoomId && (() => {
        const selectedRoom = rooms.find((r) => r.id === selectedRoomId);
        if (!selectedRoom) return null;

        const scheduleEvents: ScheduleEvent[] = selectedRoom.events.map((e) => ({
          id: e.id,
          title: e.title,
          start: e.start,
          end: e.end,
          organizerName: e.organizer_tenant_name ?? undefined,
          isMyBooking: e.is_my_booking,
          description: e.description,
        }));

        // Use free slots from API (no client-side computation)
        const scheduleFreeSlots: ScheduleFreeSlot[] = (selectedRoom.free_slots ?? []).map((s) => ({
          start: s.start,
          end: s.end,
          durationMinutes: s.duration_minutes,
        }));

        return (
          <Box
            w={320}
            style={{
              flexShrink: 0,
              background: "var(--mantine-color-body)",
              borderLeft: "1px solid var(--mantine-color-default-border)",
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 4rem)",
            }}
          >
            <Group justify="space-between" px="sm" py="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
              <div style={{ minWidth: 0 }}>
                <Text fz="sm" fw={600} truncate="end">{selectedRoom.description || selectedRoom.name}</Text>
                {selectedRoom.description && <Text fz="xs" c="dimmed">{selectedRoom.name}</Text>}
                {hasPriceInfo(selectedRoom.pricing) && (
                  <Box mt={4}>
                    <RoomPriceBadge pricing={selectedRoom.pricing} />
                  </Box>
                )}
              </div>
              <Group gap={4}>
                <ActionIcon variant="subtle" size="sm" onClick={() => navigate(`/meeting-rooms/${selectedRoomId}`)} aria-label="Open full page">
                  <Maximize2 size={16} />
                </ActionIcon>
                <ActionIcon variant="subtle" size="sm" onClick={() => setSelectedRoomAndUrl(null)} aria-label="Close sidebar">
                  <X size={16} />
                </ActionIcon>
              </Group>
            </Group>
            {selectedRoom.is_bookable ? (
              <RoomDaySchedule
                events={scheduleEvents}
                freeSlots={scheduleFreeSlots}
                date={date}
                dateLabel={date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                isToday={toISODate(date) === toISODate(new Date())}
                onBook={(start, end, title, description) => {
                  bookMutation.mutate({ roomId: selectedRoom.id, start, end, title, description });
                }}
                onCancel={(id) => cancelMutation.mutate(id)}
                compact
                pricing={selectedRoom.pricing}
                bookingNotice={approvalNoticeFor(approvalSettings, selectedRoom.pricing)}
              />
            ) : (
              <Flex align="center" justify="center" direction="column" gap="xs" flex={1} p={24}>
                <Text fz="sm" c="dimmed" ta="center">This room can't be booked.</Text>
                <Text fz="xs" c="dimmed" ta="center">Ask your office manager if you need it.</Text>
              </Flex>
            )}
          </Box>
        );
      })()}
    </Flex>
  );
}
